import { Button, Form, Modal } from "react-bootstrap"
import i18next from "i18next"
import _ from 'lodash';
import { useState } from "react";
import RequiredFormLabel from "../../RequiredFormLabel/RequiredFormLabel";
import { FileUploader } from "react-drag-drop-files";
import { OPPORTUNITY_SUPPORTED_FILE_TYPES } from "src/utils/constants";
import { toast } from "react-toastify";
import { OpportunityChecklistItemDTO } from "src/api/models/Opportunities/OpportunityChecklistItemDTO";
import { Storage } from "aws-amplify";
import { useApiClient } from "src/hooks/useApiClient";
import { useQueryClient } from "@tanstack/react-query";
import { useErrorToast } from "src/hooks/useErrorToast";
import SpinnerButton from "src/components/SpinnerButton/SpinnerButton";

type Props = {
  isOpen?: boolean;
  opportunityId: string;
  closeModal?: (reload?: boolean) => void;
  maxSize?: number;
  onSuccess?: () => void;
}

const defaultFormData = () => ({
  type: '',
  name: '',
  key: '',
  reason: '',
});

export function RequestOpportunityDocumentModal({ isOpen, closeModal = _.noop, maxSize, opportunityId, onSuccess }: Props) {
  const [formData, setFormData] = useState(defaultFormData());
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { apiClient, handleApiResponse } = useApiClient();
  const queryClient = useQueryClient();
  const { showError } = useErrorToast();

  const onClose = () => {
    setFormData(defaultFormData())
    closeModal();
  }

  const uploadFileToS3 = async () => {
    if (!uploadFile) return;

    try {
      const ext = uploadFile.name.split('.').pop();
      const key = `opportunity_uploads/${crypto.randomUUID()}.${ext}`;

      const response = await Storage.put(key, uploadFile, {
        contentType: uploadFile.type,
        bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
      });

      return `public/${response.key}`;
    } catch (e) {
      toast.error(i18next.t('error_loading_file'))
      throw e;
    }
  }

  const requestDocument = async () => {
    const dto: OpportunityChecklistItemDTO = {
      type: formData.type,
      reason: formData.reason,
    }

    if (uploadFile) {
      dto.key = await uploadFileToS3();
      dto.name = uploadFile.name;
    }

    try {
      setSubmitting(true);
      await handleApiResponse(apiClient.salesforceOpportunityController.addRequestDocument(opportunityId, dto));
      queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      onSuccess?.();
      closeModal?.();
    } catch (error) {
      showError(error, i18next.t('error_removing_attachment'));
    } finally {
      setSubmitting(false);
    }
  }

  const isDisabled = !formData.type?.trim() || !formData.reason?.trim();

  return (
    <Modal show={isOpen} size="lg">
      <Modal.Header>
        <Modal.Title>{i18next.t('request_documentation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3 mt-3">
            <RequiredFormLabel label={i18next.t('document_name')} />
            <Form.Control required onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target?.value }))} />
          </Form.Group>

          <Form.Group className="mb-3 mt-3">
            <RequiredFormLabel label={i18next.t('motivation')} />
            <Form.Control required className="py-3" as="textarea" rows={5} onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target?.value }))} />
          </Form.Group>

          <div>
            <p>{i18next.t('attach_file')}</p>
            <FileUploader
              fileOrFiles={uploadFile}
              classes="dropzone"
              types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
              handleChange={(file) => {
                if (typeof maxSize !== 'undefined' && file?.size > maxSize) {
                  toast.error(`Dimensione di caricamento massima superata (max ${maxSize / 1024 / 1024} MB)`);
                  return
                }
                setUploadFile(file);
              }}
              onTypeError={() => {
                setUploadFile(null);
                setUploadError(i18next.t('unsupported_file'));
              }}
              name="file"
            >
              <div className="text-center">
                <div className="fs-4">
                  <div>{i18next.t('click_or_drag_file')}</div>
                </div>
                <div>
                  <small>({OPPORTUNITY_SUPPORTED_FILE_TYPES.join(', ')})</small>
                </div>
              </div>

              <div className="text-center">
                {uploadFile && uploadFile?.size > 0 ? (
                  <ul className="mt-3">
                    <li key="name"> {uploadFile?.name} </li>
                  </ul>
                ) : (
                  ''
                )}
                {uploadError
                  ? <p className="mt-4 text-danger">{uploadError}</p>
                  : null
                }
              </div>
            </FileUploader>
            {uploadFile && uploadFile?.size > 0 ? (
              <div className="d-flex justify-content-end text-right">
                <Button
                  variant="danger"
                  onClick={() => {
                    setUploadFile(null);
                  }}
                  className="my-3 btn btn-sm btn-outline file-uploader-clear-btn"
                  type="button"
                >
                  {i18next.t('delete')}
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='link' onClick={onClose} disabled={submitting}>
          {i18next.t('cancel')}
        </Button>
        <Button disabled={isDisabled} onClick={requestDocument}>
          <SpinnerButton busy={submitting} label={i18next.t('proceed')} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
